(() => {
  'use strict';
  const element = document.getElementById('ephraim-book-bonuses');
  if (!element) return;
  const config = JSON.parse(element.textContent);
  const root = window.Shopify?.routes?.root || '/';
  const marker = String(config.bookProductId);
  const giftIds = new Set(config.gifts.map((gift) => String(gift.id)));
  let queue = Promise.resolve();

  const isManagedGift = (item) =>
    giftIds.has(String(item.variant_id)) && item.properties?._ephraim_bonus === marker;

  async function request(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || data.errors || data.status) throw new Error(config.errorMessage);
    return data;
  }

  async function removeGifts(cart) {
    const updates = Object.fromEntries(cart.items.filter(isManagedGift).map((item) => [item.key, 0]));
    return Object.keys(updates).length ? request('cart/update.js', { updates }) : cart;
  }

  async function reconcile({ sections = [] } = {}) {
    let cart = await request('cart.js');
    const quantity = cart.items.reduce(
      (total, item) => total + (String(item.product_id) === marker ? item.quantity : 0), 0
    );
    const available = config.gifts.every((gift) => gift.available);
    let warning = '';

    if (!quantity || !available) {
      cart = await removeGifts(cart);
      if (quantity && !available) warning = config.unavailableMessage;
    } else {
      // Mark only theme-added gifts. Separately purchased accessories stay untouched.
      const updates = {};
      const additions = [];
      for (const gift of config.gifts) {
        const lines = cart.items.filter((item) => isManagedGift(item) && String(item.variant_id) === String(gift.id));
        if (!lines.length) {
          additions.push({ id: gift.id, quantity, properties: { _ephraim_bonus: marker } });
        } else {
          lines.forEach((item, index) => {
            const target = index === 0 ? quantity : 0;
            if (item.quantity !== target) updates[item.key] = target;
          });
        }
      }
      try {
        if (Object.keys(updates).length) await request('cart/update.js', { updates });
        if (additions.length) await request('cart/add.js', { items: additions });
        cart = await request('cart.js');
        // Never label a charged item as free. Native Shopify discounts set these prices.
        const gifts = cart.items.filter(isManagedGift);
        if (gifts.length !== config.gifts.length || gifts.some((item) => item.final_line_price !== 0)) {
          cart = await removeGifts(cart);
          warning = config.unavailableMessage;
        }
      } catch (error) {
        // Shopify may partially fulfill an inventory-limited add. Clean up before checkout.
        cart = await removeGifts(await request('cart.js'));
        warning = config.unavailableMessage;
      }
    }

    if (sections.length) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', [...new Set(sections)].slice(0, 5).join(','));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(config.errorMessage);
      cart.sections = await response.json();
      if (Object.values(cart.sections).some((section) => section === null)) throw new Error(config.errorMessage);
    }
    cart.bonusWarning = warning;
    return cart;
  }

  function sync(options) {
    const result = queue.then(() => reconcile(options));
    queue = result.catch(() => {});
    return result;
  }
  window.EphraimBookBonuses = { sync };

  // Wait for reconciliation before native checkout submits. A failed cleanup blocks checkout.
  const approvedForms = new WeakSet();
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (approvedForms.has(form)) {
      approvedForms.delete(form);
      return;
    }
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    const button = event.submitter;
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      await sync();
      approvedForms.add(form);
      form.requestSubmit(button);
    } catch (error) {
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errors) { errors.setAttribute('role', 'alert'); errors.textContent = config.errorMessage; }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }, true);
})();
